import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty, get } from 'lodash';
import PropTypes from 'prop-types';
import { useLazyQuery, useMutation } from '@apollo/client';
import { LOUISIANA } from './constants/licenses';
import {
    DEFAULT_FONT_SIZE_SCALE,
    ZOOM_CONFIG,
    IS_MAZE_UNTIMED,
    IS_CAPTIONS_ENABLED,
    IS_ANIMATION_VISUAL_REDUCED,
} from './constants/accessibility';
import {
    GET_ACCESSIBILITY_CONFIG,
    SET_ACCESSIBILITY_CONFIG,
    GET_STAFF_CONFIG,
    GET_INSTITUTION_INFO,
} from './common/clients/ApolloServiceClient';
import selectors from './rootSelectors';
import { dataDogAddWarning } from './common/logging/datadog';
import { MAZE } from './constants/measure';

// Extracts only serialisable fields so we never log raw Apollo error objects to DataDog.
const formatApolloError = error => ({
    message: error?.message,
    graphQLErrorCodes: error?.graphQLErrors?.map(e => e?.extensions?.code)?.filter(Boolean),
    networkErrorStatus: error?.networkError?.statusCode,
});

export const ApolloContext = React.createContext({
    fontSizeScaleValue: DEFAULT_FONT_SIZE_SCALE,
    isLouisianaLicenseAvailable: false,
    isMazeUntimed: false,
    isCaptionsEnabled: false,
    isAnimationVisualReduced: false,
    accessibilityConfigError: false,
    accessibilityConfigLoading: false,
    retryConfig: () => {},
});

const ApolloContextProvider = ({ children }) => {
    const isLanternUser = useSelector(selectors.getIsLanternUser);
    const studentUid = useSelector(selectors.getUserId);
    const instId = useSelector(state => selectors.getInstId(state, MAZE.MEASURE));
    const [isLouisianaLicenseAvailable, setLouisianaLicenseAvailability] = useState(false);
    const [fourthWindowLabel, setFourthWindowLabel] = useState('');
    const [accessibilityConfigData, setAccessibilityConfigData] = useState({
        fontSizeScaleValue: DEFAULT_FONT_SIZE_SCALE,
        configBK: null,
        isMazeUntimed: false,
        isCaptionsEnabled: false,
        isAnimationVisualReduced: false,
    });

    // Always hold the latest accessibilityConfigData without stale closures in useCallback.
    const accessibilityConfigDataRef = useRef(accessibilityConfigData);
    accessibilityConfigDataRef.current = accessibilityConfigData;

    const [getStaffConfig, staffConfig] = useLazyQuery(GET_STAFF_CONFIG, {
        onCompleted: data => {
            if (!isEmpty(data)) {
                const hasLouisianaLicense =
                    data &&
                    data.getStaffConfig &&
                    data.getStaffConfig.licenses &&
                    data.getStaffConfig.licenses.includes(LOUISIANA);
                setLouisianaLicenseAvailability(hasLouisianaLicense);
            }
        },
        onError: error => {
            dataDogAddWarning('Get staff config error', { error: formatApolloError(error) });
        },
        // to handle caching properly
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'none',
    });

    const [getInstitutionInfo, institutionInfo] = useLazyQuery(GET_INSTITUTION_INFO, {
        onCompleted: data => {
            if (!isEmpty(data)) {
                const windowLabel = get(
                    data,
                    'getInstitutionInfo_v2.windowDates.fourthWindowLabel',
                    '',
                );
                setFourthWindowLabel(windowLabel);
            }
        },
        onError: error => {
            dataDogAddWarning('Get institution info error', { error: formatApolloError(error) });
        },
        // to handle caching properly
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'none',
    });

    const [getAccessibilityConfig, accessibilityGetterOptions] = useLazyQuery(
        GET_ACCESSIBILITY_CONFIG,
        {
            onCompleted: data => {
                if (!isEmpty(data)) {
                    const accessibilities = get(
                        data,
                        'getApplicationConfigResolvers_v2.accessibilityConfig',
                        [],
                    );
                    const scopeConfig = accessibilities.find(el => el.studentUid === studentUid);
                    const fontSizeScaleVal = get(scopeConfig, ZOOM_CONFIG, DEFAULT_FONT_SIZE_SCALE);
                    const isCaptionsEnabled = get(scopeConfig, IS_CAPTIONS_ENABLED, false);
                    const isMazeUntimed = get(scopeConfig, IS_MAZE_UNTIMED, false);
                    const isAnimationVisualReduced = get(
                        scopeConfig,
                        IS_ANIMATION_VISUAL_REDUCED,
                        false,
                    );
                    // config_bk is required by the mutation; undefined signals a first-time write.
                    const configBK = get(scopeConfig, 'config_bk', null);

                    setAccessibilityConfigData({
                        fontSizeScaleValue: fontSizeScaleVal,
                        isMazeUntimed,
                        isCaptionsEnabled,
                        isAnimationVisualReduced,
                        configBK,
                    });
                }
            },
            onError: error => {
                dataDogAddWarning('Get accessibility config error', {
                    error: formatApolloError(error),
                });
            },
            // to handle caching properly
            fetchPolicy: 'cache-and-network',
            errorPolicy: 'none',
        },
    );

    const [setAccessibilityConfig] = useMutation(SET_ACCESSIBILITY_CONFIG, {
        onError: error => {
            dataDogAddWarning('Accessibility config mutation error', {
                error: formatApolloError(error),
            });
        },
    });

    // Returns a Promise that resolves (or rejects) once the mutation settles, so callers using
    // await (e.g. toggleCaptionsVisibility, changeTextSize) are properly blocked until the API
    // responds — preventing concurrent requests and 409 conflicts.
    const setAccessibilityConfigValues = useCallback(
        (configItem, onCompletedCb = () => {}) => {
            const previousState = accessibilityConfigDataRef.current;
            const updatedState = { ...previousState, ...configItem };

            setAccessibilityConfigData(updatedState);

            return setAccessibilityConfig({
                variables: {
                    studentUid,
                    fontSizeScaleValue: updatedState.fontSizeScaleValue,
                    isCaptionsEnabled: updatedState.isCaptionsEnabled,
                    isAnimationVisualReduced: updatedState.isAnimationVisualReduced,
                    configBK: updatedState.configBK || null,
                },
            })
                .then(response => {
                    if (response) {
                        onCompletedCb();
                        const configBK = get(
                            response,
                            'data.postApplicationConfigResolvers_v2.accessibility_bk',
                        );
                        setAccessibilityConfigData(current => ({
                            ...current,
                            configBK: configBK || current.configBK,
                        }));
                    }
                })
                .catch(error => {
                    // Roll back optimistic state update on failure.
                    setAccessibilityConfigData(previousState);
                    // Surface the failure to the caller (e.g. re-enables loading state).
                    throw error;
                });
        },
        [setAccessibilityConfig, studentUid],
    );

    useEffect(() => {
        if (
            isLanternUser &&
            !staffConfig.loading &&
            isEmpty(staffConfig.data) &&
            !staffConfig.error
        ) {
            // Apollo's onError handler logs the formatted Apollo error; .catch
            // logs only the rejection point (no error details) to avoid
            // exposing PII and prevents the rejection surfacing as unhandled.
            getStaffConfig().catch(() => {
                dataDogAddWarning('GET_STAFF_CONFIG imperative call rejected');
            });
        }
    }, [isLanternUser, staffConfig.data, staffConfig.loading, staffConfig.error, getStaffConfig]);

    useEffect(() => {
        if (
            isLanternUser &&
            studentUid &&
            !accessibilityGetterOptions.loading &&
            isEmpty(accessibilityGetterOptions.data) &&
            !accessibilityGetterOptions.error
        ) {
            getAccessibilityConfig({ variables: { studentUids: studentUid } }).catch(() => {
                dataDogAddWarning('GET_ACCESSIBILITY_CONFIG imperative call rejected');
            });
        }
    }, [
        isLanternUser,
        accessibilityGetterOptions.data,
        accessibilityGetterOptions.loading,
        accessibilityGetterOptions.error,
        studentUid,
        getAccessibilityConfig,
    ]);

    useEffect(() => {
        if (
            studentUid &&
            instId &&
            !institutionInfo.loading &&
            isEmpty(institutionInfo.data) &&
            !institutionInfo.error
        ) {
            getInstitutionInfo({ variables: { instId } }).catch(() => {
                dataDogAddWarning('GET_INSTITUTION_INFO imperative call rejected');
            });
        }
    }, [
        institutionInfo.data,
        institutionInfo.loading,
        institutionInfo.error,
        instId,
        studentUid,
        getInstitutionInfo,
    ]);

    // Re-runs both boot config queries with network-only so a successful retry
    // clears their error state and unmounts AccessibilityErrorOverlay. We retry
    // staff config too because MazePassageView's untimed-MAZE gate ANDs
    // isMazeUntimed (from accessibility) with isLouisianaLicenseAvailable (from
    // staff). If only accessibility is retried, a transient failure that took
    // out both queries leaves the staff flag stuck at its false default and
    // the 3-minute timer still fires.
    const retryConfig = useCallback(() => {
        if (studentUid) {
            getAccessibilityConfig({
                variables: { studentUids: studentUid },
                fetchPolicy: 'network-only',
            });
        }
        if (isLanternUser) {
            getStaffConfig({ fetchPolicy: 'network-only' });
        }
    }, [getAccessibilityConfig, getStaffConfig, studentUid, isLanternUser]);

    const contextValue = useMemo(
        () => ({
            isLouisianaLicenseAvailable,
            fontSizeScaleValue: accessibilityConfigData.fontSizeScaleValue,
            isMazeUntimed: accessibilityConfigData.isMazeUntimed,
            isCaptionsEnabled: accessibilityConfigData.isCaptionsEnabled,
            isAnimationVisualReduced: accessibilityConfigData.isAnimationVisualReduced,
            setAccessibilityConfigValues,
            accessibilityConfigLoading: accessibilityGetterOptions.loading,
            accessibilityConfigError: Boolean(accessibilityGetterOptions.error),
            retryConfig,
            fourthWindowLabelLoading: institutionInfo.loading,
            fourthWindowLabel,
        }),
        [
            isLouisianaLicenseAvailable,
            accessibilityConfigData.fontSizeScaleValue,
            accessibilityConfigData.isMazeUntimed,
            accessibilityConfigData.isCaptionsEnabled,
            accessibilityConfigData.isAnimationVisualReduced,
            setAccessibilityConfigValues,
            accessibilityGetterOptions.loading,
            accessibilityGetterOptions.error,
            retryConfig,
            institutionInfo.loading,
            fourthWindowLabel,
        ],
    );

    return <ApolloContext.Provider value={contextValue}>{children}</ApolloContext.Provider>;
};

ApolloContextProvider.propTypes = {
    children: PropTypes.node,
};

ApolloContextProvider.defaultProps = {
    children: null,
};

export default ApolloContextProvider;
